import { RootState } from '../reducers';
import { createSelector } from 'reselect';
import {
    isLayerVisibleOnMap,
    isLayerVisibleInList,
    isMapboxLayerType,
    getSourceIdByLayerId,
} from '@utils/mapUtils';
import { DataById, NamedLayer, SourceItem } from '../map/types';
import { DeckLayerType } from '@api/enums/enums';
import { ListLayer } from '../LayersList/types';
import { SimulationState } from '@api/entities/replancity_RunnedAlgorithm';

export const selectCurrentProjectId = createSelector(
    [(state: RootState) => state.projects.currentProjectId],
    currentProjectId => currentProjectId
);

export const selectCurrentProjectModes = createSelector(
    [(state: RootState) => state.projects.allowedModes],
    allowedModes => allowedModes
);

export const selectCurrentProjectDefaultMode = createSelector(
    [(state: RootState) => state.projects.defaultMode],
    defaultMode => defaultMode
);

export const selectProjectsById = createSelector(
    [(state: RootState) => state.projects.projectsById],
    projectsById => projectsById
);

export const selectProjectById = createSelector(
    [selectProjectsById, (state: RootState, projectId: string) => projectId],
    (projectsById, projectId) => projectsById[projectId]
);

export const selectCurrentProjectBorders = createSelector(
    [selectCurrentProjectId, selectProjectsById],
    (id, projectsById) => projectsById[id]?.simulationRegionInJsonFormat
);

export const selectCurrentProjectCenter = createSelector(
    [selectCurrentProjectId, selectProjectsById],
    (id, projectsById) => projectsById[id]?.centerCoord
);

export const selectProjects = createSelector([selectProjectsById], projectsById =>
    Object.values(projectsById)
);

export const selectCurrentProjectName = createSelector(
    [selectCurrentProjectId, selectProjectsById],
    (id, projectsById) => projectsById[id]?.projectName
);

export const selectPresetsById = createSelector(
    [(state: RootState) => state.projects.presets.byId],
    presetsById => presetsById
);

export const selectPresetsIds = createSelector(
    [(state: RootState) => state.projects.presets.ids],
    presetsIds => presetsIds
);

export const selectSelectedPresetId = createSelector(
    [(state: RootState) => state.projects.presets.selectedPresetId],
    selectedPresetId => selectedPresetId
);

export const selectProjectPresets = createSelector(
    [selectPresetsIds, selectPresetsById],
    (ids, presetsById) => ids.map(id => presetsById[id])
);

export const selectPresetType = createSelector(
    [(state: RootState) => state.projects.presets.presetType],
    presetType => presetType
);

export const selectSelectedRunId = createSelector(
    [(state: RootState) => state.projects.runs.selectedRunId],
    selectedRunId => selectedRunId
);

export const selectRunsById = createSelector(
    [(state: RootState) => state.projects.runs.byId],
    runsById => runsById
);

export const selectRunIds = createSelector(
    [(state: RootState) => state.projects.runs.ids],
    runsIds => runsIds
);

export const selectRunIdToCompareWith = createSelector(
    [(state: RootState) => state.projects.runs.runIdToCompareWith],
    runsIds => runsIds
);

export const selectProjectRuns = createSelector([selectRunIds, selectRunsById], (ids, runsById) =>
    ids.map(id => runsById[id])
);

export const selectCurrentProjectSimulationState = createSelector(
    [selectProjectsById, selectCurrentProjectId],
    (projectsById, id) => {
        const { simulationState, simulationProgress } = projectsById[id] ?? {};

        return {
            simulationState,
            simulationProgress,
        };
    }
);

//----- Layers -----------------
export const selectMapboxLayerIds = createSelector(
    [(state: RootState) => state.map.mapboxglLayers.allId],
    allId => allId
);

export const selectDeckGlLayerIds = createSelector(
    [(state: RootState) => state.map.deckglLayers.allId],
    allId => allId
);

export const selectAllLayersIds = createSelector(
    [selectMapboxLayerIds, selectDeckGlLayerIds],
    (mapboxLayerIds, deckGlLayerIds) => [...mapboxLayerIds, ...deckGlLayerIds]
);

export const selectMapboxLayersById = createSelector(
    [(state: RootState) => state.map.mapboxglLayers.byId],
    byId => byId
);

export const selectDeckGlLayersById = createSelector(
    [(state: RootState) => state.map.deckglLayers.byId],
    byId => byId
);

export const selectMapboxLayerById = createSelector(
    [selectMapboxLayersById, (state: RootState, id: string) => id],
    (layersById, id) => layersById[id]
);

export const selectDeckGlLayerById = createSelector(
    [selectDeckGlLayersById, (state: RootState, id: string) => id],
    (layersById, id) => layersById[id]
);

export const selectAllLayersById = createSelector(
    [selectMapboxLayersById, selectDeckGlLayersById],
    (mapboxLayersById, deckGlLayersById) => ({ ...mapboxLayersById, ...deckGlLayersById })
);

export const selectLayerById = createSelector(
    [selectAllLayersById, (state, layerId) => layerId],
    (layersById, layerId) => layersById[layerId]
);

export const selectLayersClickabilityById = createSelector(
    [selectMapboxLayerIds, selectMapboxLayersById],
    (layerIds: string[], layersById: DataById<NamedLayer>) =>
        layerIds.reduce((acc, id) => {
            acc[id] = layersById[id].clickable && !layersById[id].alwaysLocked;
            return acc;
        }, {})
);

export const selectLayerClickabilityById = createSelector(
    [selectMapboxLayersById, (state, layerId) => layerId],
    (layersById: DataById<NamedLayer>, layerId) => layersById[layerId]?.clickable ?? false
);

//----- Sources -----------------
export const selectSourcesById = createSelector(
    [(state: RootState) => state.map.sources.byId],
    byId => byId
);

export const selectMapboxGlSourcesById = createSelector(
    [selectSourcesById],
    (sourcesById: DataById<SourceItem>) => {
        return Object.values(sourcesById).reduce((acc, source) => {
            if (isMapboxLayerType(source.type)) {
                acc[source.id] = source;
            }
            return acc;
        }, {});
    }
);

export const selectDeckGlSourcesById = createSelector(
    [selectSourcesById],
    (sourcesById: DataById<SourceItem>) => {
        return Object.values(sourcesById).reduce((acc, source) => {
            if (source.type in DeckLayerType) {
                acc[source.id] = source;
            }
            return acc;
        }, {} as DataById<SourceItem>);
    }
);

export const selectAllLayersIdsBySourceId = createSelector(
    [(state: RootState) => state.map.allLayersIdsBySourceId],
    allLayersIdsBySourceId => allLayersIdsBySourceId
);

export const selectSourceIdByLayerId = createSelector(
    [selectAllLayersIds, selectSourcesById],
    (ids, sourcesById) =>
        ids.reduce((acc, id) => {
            const sourceId = getSourceIdByLayerId(id);
            if (sourcesById[sourceId]) {
                acc[id] = sourceId;
            }
            return acc;
        }, {})
);

export const selectClickableMapboxLayersIds = createSelector(
    [selectMapboxLayerIds, selectMapboxLayersById],
    (ids, layersById) => ids.filter(id => !!layersById[id].clickable)
);

export const selectInitialAllLayersClickability = createSelector(
    [selectAllLayersIds, selectSourcesById, selectSourceIdByLayerId],
    (
        layerIds: string[],
        sourcesById: DataById<SourceItem>,
        layerIdsBySourceId: Record<string, string>
    ) =>
        layerIds.reduce((acc, id) => {
            const sourceId = layerIdsBySourceId[id];
            if (sourcesById[sourceId]) {
                acc[id] = sourcesById[sourceId].clickable;
            }
            return acc;
        }, {})
);

export const selectAllSourceIds = createSelector(
    [(state: RootState) => state.map.sources.allId],
    allId => allId
);

export const selectSourceById = createSelector(
    [selectSourcesById, (state: RootState, sourcesId: string) => sourcesId],
    (sourcesById: DataById<SourceItem>, sourceId) => sourcesById[sourceId]
);

export const selectIfSourceReloadRequired = createSelector(
    [(state: RootState) => state.map.requireToReloadSources, (state, sourceId) => sourceId],
    (requireToReloadSources, id) => requireToReloadSources[id] ?? false
);

export const selectSourcesDataById = createSelector(
    [(state: RootState) => state.map.dataBySourceId],
    dataBySourceId => dataBySourceId
);

export const selectTripsSource = createSelector(
    [selectAllSourceIds, selectSourcesById],
    (ids, sourcesById) => {
        for (const id of ids) {
            if (sourcesById[id].type === DeckLayerType.TRIPS) {
                return sourcesById[id];
            }
        }
    }
);

export const selectDeckGlSourcesWithoutTripsById = createSelector(
    [selectTripsSource, selectDeckGlSourcesById],
    (tripsSource, sourcesById) => {
        if (tripsSource?.id) {
            const { [tripsSource.id]: removedSource, ...restSources } = sourcesById;
            return restSources;
        }
        return sourcesById;
    }
);

export const selectSourcesWithoutTripsById = createSelector(
    [selectMapboxGlSourcesById, selectDeckGlSourcesWithoutTripsById],
    (mapboxGlSourcesById, deckGlSourcesById) => ({ ...mapboxGlSourcesById, ...deckGlSourcesById })
);

export const selectSourceDataById = createSelector(
    [selectSourcesDataById, (state: RootState, sourcesId: string) => sourcesId],
    (dataBySourceId, sourcesId) => dataBySourceId[sourcesId]
);

export const selectSourceDataByIdExists = createSelector(
    [selectSourceDataById],
    data => !!data.features.length
);

export const selectMapLayerIdsByEntityNames = createSelector([selectSourcesById], sourcesById => {
    return Object.entries(sourcesById)
        .filter(([id, source]) => source.calculationState !== SimulationState.DO_NOT_SHOW)
        .reduce((acc, [id, { entityName }]) => {
            acc[entityName] = id;
            return acc;
        }, {});
});

export const selectMapLayerIdByEntityName = createSelector(
    [selectMapLayerIdsByEntityNames, (_, entityName) => entityName],
    (sourceIdsByEntityName, entityName) => sourceIdsByEntityName[entityName]
);

export const selectDeckGlLayersDataById = createSelector(
    [selectDeckGlSourcesById, selectSourcesDataById],
    (sourcesByIds, sourcesDataById) => {
        return Object.keys(sourcesByIds).reduce((acc, id) => {
            acc[id] = sourcesDataById[id];
            return acc;
        }, {});
    }
);

export const selectSourceLegendsById = createSelector(
    [(state: RootState) => state.map.legendsBySourceId],
    legendsBySourceId => legendsBySourceId
);

export const selectCalculationState = createSelector(
    [(state: RootState) => state.map.calculationStateBySourceId],
    calculationStateBySourceId => calculationStateBySourceId
);

export const selectLegendsForVisibleLayersById = createSelector(
    [selectAllLayersById, selectSourceLegendsById, selectCalculationState],
    (layersByIds, legendsById, calculationStateById) => {
        return Object.entries(layersByIds).reduce((acc, [id, layer]) => {
            if (
                isLayerVisibleOnMap(layer) &&
                isLayerVisibleInList(layer, calculationStateById[id]) &&
                legendsById[id]
            ) {
                acc[id] = { ...legendsById[id] };
                acc[id]['layerName'] = layer.layerName;
            }
            return acc;
        }, {});
    }
);

export const selectSourceDistributionsById = createSelector(
    [(state: RootState) => state.map.distributionsById],
    distributionsById => distributionsById
);

export const selectDistributionsForVisibleLayersById = createSelector(
    [selectAllLayersById, selectSourceDistributionsById, selectCalculationState],
    (layersByIds, legendsById, calculationStateById) => {
        return Object.entries(layersByIds).reduce((acc, [id, layer]) => {
            if (
                isLayerVisibleOnMap(layer) &&
                isLayerVisibleInList(layer, calculationStateById[id]) &&
                legendsById[id]
            ) {
                acc[id] = legendsById[id];
            }
            return acc;
        }, {});
    }
);

export const selectListLayers = createSelector(
    [selectAllLayersIds, selectAllLayersById, selectSourcesById, selectCalculationState],
    (layersIds, layersByIds, sourcesById, calculationStateById) => {
        return {
            layersByGroup: layersIds.reduce((acc, id) => {
                const layer = layersByIds[id];
                const { group, dayTimeDependent } = sourcesById[layer.source];

                if (!acc[group]) {
                    acc[group] = [];
                }
                acc[group].push({
                    ...layer,
                    group,
                    dayTimeDependent,
                    state: calculationStateById[layer.source],
                });
                return acc;
            }, {}) as DataById<ListLayer[]>,
            layersIds,
        };
    }
);

export const selectProjectDateTypes = createSelector(
    [(state: RootState) => state.map.dateTypesById],
    dateTypesById => dateTypesById
);

export const selectCurrentDateType = createSelector(
    [selectProjectDateTypes, (state: RootState) => state.map.selectedDateTypeId],
    (dateTypesById, selectedDateTypeId) => dateTypesById[selectedDateTypeId]
);

export const selectLayerCalculationState = createSelector(
    [selectCalculationState, (state: RootState, id: string) => id],
    (calculationStateBySourceId, id) => calculationStateBySourceId[id]
);

export const selectMapConfigCoordinates = createSelector(
    [selectProjectsById, selectCurrentProjectId],
    (projectsById, projectId) => {
        const { centerCoord } = projectsById[projectId] ?? {};
        if (centerCoord) {
            const [latitude, longitude] = centerCoord.split(',');
            return { latitude: parseFloat(latitude), longitude: parseFloat(longitude) };
        }
        return { latitude: 0, longitude: 0 };
    }
);

export const selectViewPortCoordinates = createSelector(
    [(state: RootState) => state.map.viewPortCoordinates],
    viewPortCoordinates => viewPortCoordinates
);

export const selectIsViewPortFixed = createSelector(
    [(state: RootState) => state.map.isViewPortFixed],
    isViewPortFixed => isViewPortFixed
);

export const selectPointToFlyTo = createSelector(
    [(state: RootState) => state.map.flyToPoint],
    flyToPoint => flyToPoint
);

export const selectBoundsToFit = createSelector(
    [(state: RootState) => state.map.boundsToFit],
    boundsToFit => boundsToFit
);

export const selectRequireToHighlightFeatures = createSelector(
    [(state: RootState) => state.map.requireToHighlightFeatures],
    requireToHighlightFeatures => requireToHighlightFeatures
);

export const selectRedirectOnFeatureSelection = createSelector(
    [(state: RootState) => state.map.redirectOnFeatureSelection],
    redirectOnFeatureSelection => redirectOnFeatureSelection
);

export const selectMapConfigReloadRequired = createSelector(
    [(state: RootState) => state.map.mapConfigReloadRequired],
    mapConfigReloadRequired => mapConfigReloadRequired
);

//------------ ShowInfo -----------------------------

export const selectSelectedFeatures = createSelector(
    [(state: RootState) => state.showInfo.selectedFeatures],
    selectedFeatures => selectedFeatures
);

export const selectSelectedCoordinates = createSelector(
    [(state: RootState) => state.showInfo.lngLat],
    lngLat => lngLat
);

export const selectSelectedFeaturesForResponsiveLayer = createSelector(
    [selectSelectedFeatures, selectSelectedCoordinates, selectSourceById],
    (features, lngLat, source) => {
        const { updateOnFeatureSelection } = source;
        if (!updateOnFeatureSelection) {
            return null;
        }

        return { features, lngLat };
    }
);

//------------ Draw -----------------------------
export const selectIsDrawMode = createSelector(
    [(state: RootState) => state.draw.isDrawMode],
    isDrawMode => isDrawMode
);

export const selectEnabledFeaturesEditing = createSelector(
    [(state: RootState) => state.draw.editingEnabled],
    editingEnabled => editingEnabled
);

export const selectDrawMode = createSelector(
    [(state: RootState) => state.draw.drawMode],
    drawMode => drawMode
);

export const selectSnapMode = createSelector(
    [(state: RootState) => state.draw.snapMode],
    snapMode => snapMode
);

export const selectCutMode = createSelector(
    [(state: RootState) => state.draw.cutMode],
    cutMode => cutMode
);

export const selectDrawnFeatures = createSelector(
    [(state: RootState) => state.draw.drawnFeatures],
    drawnFeatures => drawnFeatures
);

//------------ Entity -----------------------------
export const selectEntities = createSelector(
    [(state: RootState) => state.entity.dataByEntityName],
    dataByEntityName => dataByEntityName
);

export const selectEntity = createSelector(
    [selectEntities, (state: RootState, entityName: string) => entityName],
    (dataByEntityName, entityName) => dataByEntityName[entityName]
);

export const selectFormEntities = createSelector(
    [(state: RootState) => state.entity.formDataByEntityName],
    formDataByEntityName => formDataByEntityName
);

export const selectFormEntity = createSelector(
    [selectFormEntities, (state: RootState, entityName: string) => entityName],
    (formDataByEntityName, entityName) => formDataByEntityName[entityName]?.data
);

export const selectIsFormEntityChanged = createSelector(
    [selectFormEntities, (state: RootState, entityName: string) => entityName],
    (formDataByEntityName, entityName) => formDataByEntityName[entityName]?.isChanged ?? false
);

export const selectIsAnyFormEntityChanged = createSelector([selectFormEntities], formEntities =>
    Object.values(formEntities).some(entity => entity.isChanged)
);

export const selectEntityMetadata = createSelector(
    [
        (state: RootState) => state.entity.metadataByEntityName,
        (state: RootState, entityName: string) => entityName,
    ],
    (metadataByEntityName, entityName) => metadataByEntityName[entityName]
);

export const selectFeaturesWithParentsByEntityName = createSelector(
    [(state: RootState) => state.entity.featuresWithParentByEntityName],
    featuresWithParentByEntityName => featuresWithParentByEntityName
);

export const selectFeatureWithParentByEntityName = createSelector(
    [selectFeaturesWithParentsByEntityName, (state: RootState, entityName: string) => entityName],
    (featuresWithParentByEntityName, entityName) => featuresWithParentByEntityName[entityName]
);

//------------ Road route checker -----------------------------
export const selectRoadRouteCheckerMarkersCoordinates = createSelector(
    [(state: RootState) => state.roadRouteCheck],
    roadRouteCheck => roadRouteCheck
);

export const selectRoadRouteCheckerPoint = createSelector(
    [
        selectRoadRouteCheckerMarkersCoordinates,
        (state: RootState, markerElementId: string) => markerElementId,
    ],
    (roadRouteCheck, markerElementId) => roadRouteCheck[markerElementId]
);

//------------ KeplerGL -----------------------------
export const selectKeplerDatasets = createSelector(
    [(state: RootState) => state.keplerGl?.map.visState.datasets],
    datasets => datasets
);
